import { Link } from "react-router";
import { useAuth } from "../../../lib/auth";
import { consoleApi, type ConsoleQueueCount } from "../../../lib/admin-console-api";
import { useAppPreferences } from "../../../lib/preferences";
import { EventRow } from "../activity";
import { LoadState, PageHeader, Section, useFormatters, useLoad } from "../ui";

const QUEUE_CARDS: Array<{ key: ConsoleQueueCount["key"]; label: string; to: string; help: string }> = [
  { key: "provider_registrations", label: "Provider registrations", to: "/admin/approvals?type=providers", help: "New businesses waiting for a decision" },
  { key: "venue_additions", label: "New venues", to: "/admin/approvals?type=venues", help: "Venues waiting to go live" },
  { key: "venue_removals", label: "Venue removals", to: "/admin/approvals?type=removals", help: "Owners asking to remove a venue" },
  { key: "tournaments", label: "Tournaments", to: "/admin/approvals?type=tournaments", help: "Venue tournaments waiting to be shown to players" },
  { key: "reviews", label: "Reviews to moderate", to: "/admin/moderation?type=reviews", help: "Player reviews waiting to be published" },
  { key: "reports", label: "Reports", to: "/admin/moderation?type=reports", help: "Reviews or authors reported by players" },
  { key: "deletion_requests", label: "Account deletions", to: "/admin/deletion-requests", help: "Players asking to delete their account" },
];

export function HomePage() {
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const { age } = useFormatters();
  const home = useLoad(() => consoleApi.home(), []);
  const activity = useLoad(() => consoleApi.listAudit({ limit: 8 }), []);
  const isAdmin = user?.role === "admin";
  const byKey = new Map((home.data?.queues ?? []).map((queue) => [queue.key, queue]));
  const waiting = (home.data?.queues ?? []).reduce((sum, queue) => sum + queue.count, 0);

  return (
    <div className="console-page">
      <PageHeader
        description={home.data ? (waiting > 0 ? t("{count} items are waiting for the team.", { count: waiting }) : t("All queues are clear.")) : null}
        kicker={t("Operations console")}
        title={t("Good to see you, {name}", { name: user?.display_name?.split(" ")[0] ?? t("team") })}
      />
      <LoadState error={home.error} loading={home.loading} onRetry={() => void home.reload()} isEmpty={!home.data}>
        <div className="console-card-grid">
          {QUEUE_CARDS.map((card) => {
            const queue = byKey.get(card.key);
            const count = queue?.count ?? 0;
            return (
              <Link className="console-queue-card" data-waiting={count > 0 ? "true" : undefined} key={card.key} to={card.to}>
                <span className="console-queue-count">{count}</span>
                <span className="console-queue-label">{t(card.label)}</span>
                <span className="console-muted">
                  {count > 0 && queue?.oldest_at ? t("Oldest waiting {age}", { age: age(queue.oldest_at) ?? "" }) : t(card.help)}
                </span>
              </Link>
            );
          })}
        </div>
        {isAdmin && home.data ? (
          <div className="console-card-grid">
            <Link className="console-queue-card" data-waiting={home.data.overdue_settlements > 0 ? "true" : undefined} to="/admin/settlements">
              <span className="console-queue-count">{home.data.overdue_settlements}</span>
              <span className="console-queue-label">{t("Providers with overdue commission")}</span>
              <span className="console-muted">{t("Issued statements past their due date")}</span>
            </Link>
            <Link className="console-queue-card" data-waiting={home.data.venues_without_commission > 0 ? "true" : undefined} to="/admin/venues?commission=missing">
              <span className="console-queue-count">{home.data.venues_without_commission}</span>
              <span className="console-queue-label">{t("Live venues without commission terms")}</span>
              <span className="console-muted">{t("Sessions there earn gaamed nothing until terms are set")}</span>
            </Link>
          </div>
        ) : null}
      </LoadState>

      <Section
        actions={<Link className="console-link" to="/admin/audit">{t("Open audit log")}</Link>}
        title={t("Recent activity")}
      >
        <LoadState
          empty={t("No recorded activity yet.")}
          error={activity.error}
          isEmpty={(activity.data?.events ?? []).length === 0}
          loading={activity.loading}
          onRetry={() => void activity.reload()}
        >
          <ol className="console-timeline">
            {(activity.data?.events ?? []).map((event) => <EventRow event={event} key={event.id} showEntity />)}
          </ol>
        </LoadState>
      </Section>
    </div>
  );
}
